import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Check, FileText, Upload } from "@phosphor-icons/react";
import type { ResumeData } from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useImportResume } from "@/client/services/resume/import";
import { useParseResume } from "@/client/services/resume/parse";
import { useDialog } from "@/client/stores/dialog";

const formSchema = z.object({
  file: z.instanceof(File),
});

type FormValues = z.infer<typeof formSchema>;

type ParseState = "idle" | "parsing" | "success" | "error";

export const ParseImportDialog = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isOpen, close } = useDialog("parse-import");
  const { parseResume, loading: parseLoading } = useParseResume();
  const { importResume, loading: importLoading } = useImportResume();

  const [parseState, setParseState] = useState<ParseState>("idle");
  const [parsedData, setParsedData] = useState<ResumeData | null>(null);
  const [progress, setProgress] = useState(0);

  const loading = parseLoading || importLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen]);

  const onReset = () => {
    form.reset();
    setParseState("idle");
    setParsedData(null);
    setProgress(0);
  };

  const onParseAndImport = async () => {
    try {
      const { file } = formSchema.parse(form.getValues());
      
      setParseState("parsing");
      setProgress(20);

      // 解析简历文件
      const parsedResumeData = await parseResume(file);
      setProgress(60);
      setParsedData(parsedResumeData);
      setParseState("success");
      setProgress(80);

      // 导入解析后的数据
      const importedResume = await importResume({ data: parsedResumeData });
      setProgress(100);

      toast({
        variant: "success",
        title: t`Resume parsed and imported successfully!`,
        description: t`Your resume has been automatically parsed and is ready for editing.`,
      });

      // 导航到编辑页面
      if (importedResume?.id) {
        navigate(`/builder/${importedResume.id}`);
      }

      close();
    } catch (error: unknown) {
      setParseState("error");
      setProgress(0);
      
      toast({
        variant: "error",
        title: t`Failed to parse resume`,
        description: error instanceof Error ? error.message : t`An unexpected error occurred while parsing your resume.`,
      });
    }
  };

  const getProgressText = () => {
    if (progress <= 20) return t`Uploading file...`;
    if (progress <= 60) return t`Parsing resume content...`;
    if (progress <= 80) return t`Converting data...`;
    return t`Importing resume...`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <Form {...form}>
          <form className="space-y-4">
            {/* <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <FileText />
                  <h2>{t`Smart Resume Import`}</h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                {t`Upload your resume file (PDF, DOC, DOCX) and we'll automatically extract and organize the information for you.`}
              </DialogDescription>
            </DialogHeader> */}

            <FormField
              name="file"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Resume File`}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="file"
                        onChange={(event) => {
                          if (!event.target.files?.length) return;
                          field.onChange(event.target.files[0]);
                          setParseState("idle");
                        }}
                        disabled={loading}
                        className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                      />
                      <Upload className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </div>
                  </FormControl>
                  <FormDescription>
                    {t`支持上传任何类型的文件，最大10MB`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AnimatePresence>
              {parseState === "parsing" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3"
                >
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{getProgressText()}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300 ease-out" 
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {parseState === "success" && parsedData && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800"
                >
                  <div className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-200">
                        {t`Resume parsed successfully!`}
                      </h4>
                      <p className="text-sm text-green-600 dark:text-green-300">
                        {t`Found ${Object.keys(parsedData.sections.education.items).length} education entries, ${Object.keys(parsedData.sections.experience.items).length} work experiences, and ${Object.keys(parsedData.sections.skills.items).length} skills.`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {parseState === "error" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800"
                >
                  <div className="text-sm text-red-600 dark:text-red-300">
                    {t`Failed to parse the resume. Please check if the file is valid and try again.`}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <DialogFooter>
              <AnimatePresence presenceAffectsLayout>
                {parseState === "idle" && (
                  <Button
                    type="button"
                    onClick={onParseAndImport}
                    disabled={!form.watch("file") || loading}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {t`Parse & Import Resume`}
                  </Button>
                )}

                {parseState === "parsing" && (
                  <Button type="button" disabled>
                    {t`Processing...`}
                  </Button>
                )}

                {parseState === "error" && (
                  <Button type="button" variant="secondary" onClick={onReset}>
                    {t`Try Again`}
                  </Button>
                )}
              </AnimatePresence>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};